import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Audio formats we ask the browser for, best first. Chrome and Firefox record
 * WebM/Opus; Safari (desktop and the iOS WebView the navigator runs in) only
 * records MP4 until 18.4. Both are transcribed by Gemini as uploaded, so the
 * AI step needs no conversion (PRTL-993).
 */
const RECORDING_FORMATS = [
    { mimeType: "audio/webm;codecs=opus", extension: "webm" },
    { mimeType: "audio/webm", extension: "webm" },
    { mimeType: "audio/mp4", extension: "m4a" },
] as const;

/** The first format this browser can record, or null to let it choose. */
export function pickRecordingFormat(
    isTypeSupported: (type: string) => boolean = (type) =>
        typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type),
): (typeof RECORDING_FORMATS)[number] | null {
    return RECORDING_FORMATS.find((f) => isTypeSupported(f.mimeType)) ?? null;
}

/** File extension for whatever container the recorder actually produced. */
function extensionFor(mimeType: string): string {
    if (mimeType.includes("mp4") || mimeType.includes("aac")) return "m4a";
    if (mimeType.includes("ogg")) return "ogg";
    return "webm";
}

/** Rejection reason when a recording is cancelled rather than stopped. */
const RECORDING_CANCELLED = "recording-cancelled";

interface AudioRecorderState {
    isRecording: boolean;
    duration: number;
    error: string | null;
}

/** The rejection a cancelled recording settles with; callers treat it as "nothing to upload". */
function cancelledError(): Error {
    return new Error(RECORDING_CANCELLED);
}

/**
 * Microphone recording for comment and discussion composers, shared by web and
 * the navigator. `startRecording` resolves with the finished file when
 * `stopRecording` is called, and rejects with `RECORDING_CANCELLED` when the
 * recording is cancelled — including while the microphone permission prompt is
 * still open, and when the component unmounts — so callers never await a
 * promise that cannot settle, and nothing is uploaded after the user left.
 */
export const useAudioRecorder = () => {
    const [state, setState] = useState<AudioRecorderState>({
        isRecording: false,
        duration: 0,
        error: null,
    });

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const rejectRef = useRef<((reason: Error) => void) | null>(null);
    /**
     * Bumped by every start and cancel. A start that finds it changed after the
     * permission prompt was cancelled while waiting, and must release the mic.
     */
    const attemptRef = useRef(0);
    /** A start is waiting on the permission prompt or recording. */
    const busyRef = useRef(false);

    const releaseDevices = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];
    }, []);

    /** Abandon whatever is in progress without producing a file. */
    const abandon = useCallback(() => {
        attemptRef.current++;
        busyRef.current = false;
        const recorder = mediaRecorderRef.current;
        if (recorder) {
            // Detach first: stopping the recorder would otherwise hand the
            // caller a file to upload.
            recorder.onstop = null;
            if (recorder.state === "recording") recorder.stop();
        }
        rejectRef.current?.(cancelledError());
        rejectRef.current = null;
        releaseDevices();
    }, [releaseDevices]);

    // Unmounting mid-recording is a cancel, not a stop.
    useEffect(() => abandon, [abandon]);

    const startRecording = useCallback(async (): Promise<File> => {
        if (busyRef.current) throw cancelledError(); // a second tap while starting
        busyRef.current = true;
        const attempt = ++attemptRef.current;
        try {
            setState({ isRecording: true, duration: 0, error: null });
            chunksRef.current = [];

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (attempt !== attemptRef.current) {
                // Cancelled (or unmounted) while the permission prompt was open.
                stream.getTracks().forEach((track) => track.stop());
                throw cancelledError();
            }
            streamRef.current = stream;

            const format = pickRecordingFormat();
            const mediaRecorder = format
                ? new MediaRecorder(stream, { mimeType: format.mimeType })
                : new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            const filePromise = new Promise<File>((resolve, reject) => {
                rejectRef.current = reject;
                mediaRecorder.onstop = () => {
                    rejectRef.current = null;
                    busyRef.current = false;
                    // The container the browser actually produced, without codec
                    // parameters, so the upload's content type matches the bytes.
                    const type = (mediaRecorder.mimeType || format?.mimeType || "audio/webm").split(
                        ";",
                    )[0];
                    const blob = new Blob(chunksRef.current, { type });
                    resolve(
                        new File([blob], `recording-${Date.now()}.${extensionFor(type)}`, { type }),
                    );
                };
            });

            mediaRecorder.start();
            intervalRef.current = setInterval(() => {
                setState((prev) => ({ ...prev, duration: prev.duration + 1 }));
            }, 1000);

            return filePromise;
        } catch (err) {
            if (err instanceof Error && err.message === RECORDING_CANCELLED) throw err;
            busyRef.current = false;
            releaseDevices();
            const message = err instanceof Error ? err.message : "Failed to access microphone";
            setState({ isRecording: false, duration: 0, error: message });
            throw err;
        }
    }, [releaseDevices]);

    const cancelRecording = useCallback(() => {
        abandon();
        setState({ isRecording: false, duration: 0, error: null });
    }, [abandon]);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        // Still waiting on the permission prompt: nothing has been recorded.
        if (!recorder) {
            cancelRecording();
            return;
        }
        if (recorder.state === "recording") recorder.stop();
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        setState((prev) => ({ ...prev, isRecording: false }));
    }, [cancelRecording]);

    return {
        isRecording: state.isRecording,
        duration: state.duration,
        error: state.error,
        startRecording,
        stopRecording,
        cancelRecording,
    };
};
